import { useCallback, useRef } from 'react';
import { useMutation } from 'react-relay';
import type { ExperimentsRecordExperimentsSpeculativeDataMutation } from '../graphQLOperations/__generated__/ExperimentsRecordExperimentsSpeculativeDataMutation.graphql';
import { recordExperimentSpeculativeData } from '../graphQLOperations/Experiments';
import type { EmotionPickerValue } from '../components/BrainBreak/EmotionPicker';
import { useObservationLogger, type Observation } from '../experiments/useObservationLogger';
import { BRAIN_BREAK_FLAG_NAME, COOLDOWN_VARIANT_NAME } from '../models/BrainBreak';

/** Subject for rows about the poll itself rather than about a measure. */
const POLL_SESSION_SUBJECT = 'poll_session';
/** Subject for the emotion picker. The rotating item's subject comes from its question type. */
const EMOTION_SUBJECT = 'emotion';

export interface BrainBreakRecordingInput {
  questionId: string;
  questionType: string;
  confidenceValue: number;
  isReverseScored?: boolean;
  emotion: Pick<EmotionPickerValue, 'id' | 'valence' | 'arousal'>;
  triggerPlacement: string | null;
}

export interface ValidationError {
  field: string;
  message: string;
}

export function validateBrainBreakInput(input: BrainBreakRecordingInput): ValidationError[] {
  const errors: ValidationError[] = [];

  if (input.confidenceValue < 0 || input.confidenceValue > 4) {
    errors.push({
      field: 'confidenceValue',
      message: `Expected 0-4, got ${input.confidenceValue}`,
    });
  }

  if (!input.emotion.id || input.emotion.id.trim() === '') {
    errors.push({
      field: 'emotion.id',
      message: 'Emotion ID is required',
    });
  }

  if (input.emotion.valence < -2 || input.emotion.valence > 2) {
    errors.push({
      field: 'emotion.valence',
      message: `Expected -2 to 2, got ${input.emotion.valence}`,
    });
  }

  if (input.emotion.arousal < -2 || input.emotion.arousal > 2) {
    errors.push({
      field: 'emotion.arousal',
      message: `Expected -2 to 2, got ${input.emotion.arousal}`,
    });
  }

  if (!input.questionId || input.questionId.trim() === '') {
    errors.push({
      field: 'questionId',
      message: 'Question ID is required',
    });
  }

  if (!input.questionType || input.questionType.trim() === '') {
    errors.push({
      field: 'questionType',
      message: 'Question type is required',
    });
  }

  return errors;
}

export interface RecordingResult {
  success: boolean;
  recordedCount: number;
  failedObservations: string[];
  speculativeSaveSuccess: boolean;
}

export interface UseBrainBreakRecorderOptions {
  onComplete?: (result: RecordingResult) => void;
}

const asSubject = (value: string): string => value.replace(/-/g, '_');

export function buildObservations(input: BrainBreakRecordingInput): Observation[] {
  const observations: Observation[] = [
    {
      subject: asSubject(input.questionType),
      type: `response_${input.questionId}`,
      value: input.isReverseScored ? input.confidenceValue * -1 : input.confidenceValue,
    },
    { subject: EMOTION_SUBJECT, type: 'valence', value: input.emotion.valence },
    { subject: EMOTION_SUBJECT, type: 'arousal', value: input.emotion.arousal },
    { subject: EMOTION_SUBJECT, type: `emoji_${input.emotion.id}` },
    { subject: POLL_SESSION_SUBJECT, type: 'poll_completed' },
  ];

  if (input.triggerPlacement) {
    observations.push({
      subject: POLL_SESSION_SUBJECT,
      type: `placement_${asSubject(input.triggerPlacement)}`,
    });
  }

  return observations;
}

export function useBrainBreakRecorder(options: UseBrainBreakRecorderOptions) {
  const { logObservations } = useObservationLogger();

  const [commitSpeculativeData] = useMutation<ExperimentsRecordExperimentsSpeculativeDataMutation>(
    recordExperimentSpeculativeData,
  );

  const isRecordingRef = useRef(false);
  const onCompleteRef = useRef(options.onComplete);
  onCompleteRef.current = options.onComplete;

  const record = useCallback(
    (input: BrainBreakRecordingInput): boolean => {
      const validationErrors = validateBrainBreakInput(input);
      if (validationErrors.length > 0) {
        onCompleteRef.current?.({
          success: false,
          recordedCount: 0,
          failedObservations: validationErrors.map(e => e.field),
          speculativeSaveSuccess: false,
        });
        return false;
      }

      // Ref provides sync check to prevent race conditions
      if (isRecordingRef.current) {
        return false;
      }

      isRecordingRef.current = true;

      const observations = buildObservations(input);

      // Cooldown still keys off the real flag name — it is a lookup, not analytics.
      const speculativePromise = new Promise<boolean>(resolve => {
        commitSpeculativeData({
          variables: {
            input: {
              input: {
                flagName: BRAIN_BREAK_FLAG_NAME,
                variantName: COOLDOWN_VARIANT_NAME,
                payload: JSON.stringify({
                  lastCompletionDate: new Date().toISOString(),
                  triggerPlacement: input.triggerPlacement,
                  questionType: input.questionType,
                }),
              },
            },
          },
          onCompleted: () => resolve(true),
          onError: () => resolve(false),
        });
      });

      Promise.all([logObservations('affect_metrics', observations), speculativePromise])
        .then(([observationResult, speculativeSaveSuccess]) => {
          isRecordingRef.current = false;

          onCompleteRef.current?.({
            success: observationResult.failed.length === 0,
            recordedCount: observationResult.logged.length,
            failedObservations: observationResult.failed,
            speculativeSaveSuccess,
          });
        })
        .catch(() => {
          isRecordingRef.current = false;
        });

      return true;
    },
    [commitSpeculativeData, logObservations],
  );

  return { record };
}
